import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState, AppDispatch } from "../../store/store";
import { Typography } from "@material-tailwind/react";
import moment from "moment";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import {
  fetchNewsletterSubscribers,
  exportAllNewsletterSubscribers,
  deleteNewsletterSubscriber,
} from "../../store/NewsletterSlice";
import { NewsletterSubscriber } from "../../interfaces/Newsletter";

const NewsletterView = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [isExporting, setIsExporting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data, loading } = useSelector((state: RootState) => state.newsletter);

  const TABLE_HEAD = [
    "#",
    t("adminNewsletter.table.email", "EMAIL"),
    t("adminNewsletter.table.status", "STATUS"),
    t("adminNewsletter.table.dateSubscribed", "DATE SUBSCRIBED"),
    t("adminNewsletter.table.actions", "ACTIONS"),
  ];

  const debouncedSearchHandler = useRef(
    debounce((query: string) => {
      setDebouncedSearch(query);
      setPage(1);
    }, 400)
  ).current;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearch(val);
    debouncedSearchHandler(val);
  };

  const handleClearSearch = () => {
    setSearch("");
    setDebouncedSearch("");
    setPage(1);
  };

  useEffect(() => {
    dispatch(fetchNewsletterSubscribers({ page, limit, search: debouncedSearch }));
  }, [dispatch, page, limit, debouncedSearch]);

  const handleDelete = async (id: string, email: string) => {
    if (window.confirm(t("adminNewsletter.confirmDelete", `Are you sure you want to remove ${email} from subscribers?`))) {
      setDeletingId(id);
      try {
        await dispatch(deleteNewsletterSubscriber(id)).unwrap();
        toast.success(t("adminNewsletter.deleteSuccess", "Subscriber removed successfully"));
      } catch (err: any) {
        toast.error(err || t("adminNewsletter.deleteError", "Failed to remove subscriber"));
      } finally {
        setDeletingId(null);
      }
    }
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const result = await dispatch(exportAllNewsletterSubscribers()).unwrap();
      if (!result || result.length === 0) {
        toast.info(t("adminNewsletter.noSubscribersExport", "No subscribers to export"));
        setIsExporting(false);
        return;
      }

      // Generate CSV string
      const headers = ["ID", "Email", "Status", "Date Subscribed"];
      const rows = result.map((sub: NewsletterSubscriber) => [
        sub.id,
        `"${sub.email}"`,
        sub.status,
        sub.createdAt ? moment(sub.createdAt).format("YYYY-MM-DD HH:mm:ss") : "",
      ]);

      const csvContent =
        "data:text/csv;charset=utf-8," +
        [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute(
        "download",
        `newsletter_subscribers_${moment().format("YYYY-MM-DD")}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(t("adminNewsletter.exportSuccess", "Subscribers exported successfully!"));
    } catch (error: any) {
      toast.error(t("adminNewsletter.exportError", "Failed to export CSV"));
    } finally {
      setIsExporting(false);
    }
  };

  const subscribers = data?.subscribers || [];
  const total = data?.total || 0;
  const totalPages = data?.totalPages || 1;

  return (
    <div className="w-full h-full flex flex-col overflow-hidden">
      {/* Search & Export Toolbar */}
      <div className="p-4 bg-white border-b border-gray-100 flex flex-wrap items-center justify-between gap-4">
        <div className="relative w-72">
          <input
            type="text"
            value={search}
            onChange={handleSearchChange}
            placeholder={t("adminNewsletter.searchPlaceholder", "Search subscriber email...")}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#B4804A]"
          />
          {search && (
            <button
              onClick={handleClearSearch}
              className="absolute right-2.5 top-2 text-gray-400 hover:text-gray-600 text-sm cursor-pointer"
            >
              ✕
            </button>
          )}
        </div>

        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 font-medium">
            {t("adminNewsletter.totalCount", "Total:")}{" "}
            <strong className="text-gray-800">{total}</strong>
          </span>
          <button
            onClick={handleExportCSV}
            disabled={isExporting || total === 0}
            className="flex items-center gap-2 bg-[#041B44] hover:bg-[#031535] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 cursor-pointer"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            {isExporting
              ? t("adminNewsletter.exporting", "Exporting...")
              : t("adminNewsletter.exportCSV", "Export to CSV")}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="w-full flex-1 overflow-x-auto overflow-y-auto">
        <table className="w-full min-w-[650px] table-auto text-left">
          <thead className="sticky top-0 bg-white z-10">
            <tr className="bg-[#F8F9FA] text-left border-b-2 border-gray-100">
              {TABLE_HEAD.map((head, i) => (
                <th
                  key={i}
                  className="py-4 px-6 font-bold text-[#7E8299] uppercase text-xs whitespace-nowrap"
                >
                  <Typography
                    variant="small"
                    className="font-semibold leading-none text-[#7E8299] opacity-70 whitespace-nowrap"
                  >
                    {head}
                  </Typography>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={5} className="text-center py-12 text-gray-500 font-medium">
                  {t("common.loading", "Loading...")}
                </td>
              </tr>
            ) : subscribers.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center py-12 text-gray-500 font-medium">
                  {t("adminNewsletter.noSubscribers", "No newsletter subscribers found")}
                </td>
              </tr>
            ) : (
              subscribers.map((sub, index) => (
                <tr
                  key={sub.id || index}
                  className="hover:bg-gray-50/50 transition-colors"
                >
                  <td className="py-4 px-6 whitespace-nowrap text-sm text-gray-500 font-medium">
                    {(page - 1) * limit + index + 1}
                  </td>
                  <td className="py-4 px-6 whitespace-nowrap">
                    <Typography
                      variant="small"
                      color="black"
                      className="font-medium text-sm"
                    >
                      {sub.email}
                    </Typography>
                  </td>
                  <td className="py-4 px-6 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        sub.status === "subscribed"
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {sub.status === "subscribed"
                        ? t("adminNewsletter.subscribed", "Subscribed")
                        : t("adminNewsletter.unsubscribed", "Unsubscribed")}
                    </span>
                  </td>
                  <td className="py-4 px-6 whitespace-nowrap text-sm text-gray-600">
                    {sub.createdAt
                      ? moment(sub.createdAt).format("DD MMM, YYYY HH:mm")
                      : "-"}
                  </td>
                  <td className="py-4 px-6 whitespace-nowrap">
                    <button
                      onClick={() => handleDelete(sub.id, sub.email)}
                      disabled={deletingId === sub.id}
                      className="text-red-500 hover:text-red-700 font-semibold text-sm transition-colors cursor-pointer"
                    >
                      {deletingId === sub.id
                        ? t("adminNewsletter.deleting", "Removing...")
                        : t("adminNewsletter.delete", "Remove")}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      {totalPages > 1 && (
        <div className="p-4 bg-white border-t border-gray-100 flex items-center justify-between">
          <span className="text-xs text-gray-500">
            {t("adminNewsletter.page", "Page")} {page} / {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              className="px-3 py-1 text-xs border rounded hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
            >
              {t("adminNewsletter.prev", "Previous")}
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || loading}
              className="px-3 py-1 text-xs border rounded hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
            >
              {t("adminNewsletter.next", "Next")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsletterView;
